/**
 * SessionTree — hierarchical tree view of proposal sessions.
 *
 * Root node = "Live Graph". Children are sessions whose dependsUponSession
 * is empty or "live". Nested children follow the DEPENDS_UPON chain.
 *
 * Clicking a node selects it and triggers a shadow-graph preview.
 * Action buttons per status:
 *   open      → Resume (reuse session) / Cancel (reject & discard)
 *   submitted → Approve (only when dependent on live) / Reject
 *   All non-open → Modify (start child session)
 */
import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ProposalSession, SessionTree as SessionTreeModel } from '../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SessionTreeNode {
  session: ProposalSession | null; // null = Live Graph root
  children: SessionTreeNode[];
}

export interface SessionTreeProps {
  tree: SessionTreeModel | null;
  activeSessionId?: string | null; // currently active edit session
  expandedSummarySessionIds?: Record<string, boolean>;
  expandedStepsSessionIds?: Record<string, boolean>;
  sessionSummaries?: Record<string, string>;
  sessionStepContent?: Record<string, React.ReactNode>;
  onToggleSummary?: (sessionId: string) => void;
  onToggleSteps?: (sessionId: string) => void;
  onShowSourceText?: (sessionId: string) => void;
  onShowTrace?: (sessionId: string) => void;
  /** Resume an existing open session (re-enter edit mode). */
  onResume?: (sessionId: string) => void;
  /** Cancel/discard an open session. */
  onCancel?: (sessionId: string) => void;
  /** Start a new child session that depends on the given session. */
  onModify?: (parentSessionId: string) => void;
  /** Approve a submitted session (only when it depends on live). */
  onApprove?: (sessionId: string) => void;
  /** Reject a session. */
  onReject?: (sessionId: string) => void;
  /** Submit (save) the currently active session. */
  onSubmitSession?: () => void;
  /** Cancel the currently active session. */
  onCancelActiveSession?: () => void;
  /** Session ID for which approve should be disabled (has rejected steps). */
  approveBlockedSessionId?: string;
  /** Session ID for which the submitted-session action should remain Reject instead of Reset. */
  rejectModeSessionId?: string;
  /** True while an approve/reject/cancel is in-flight. */
  busy?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
}

/** Derive a friendly first name from an email address, matching the auth button display. */
function friendlyName(email: string): string {
  if (!email) return '';
  return email.split('@')[0].split('.')[0].replace(/^(.)/, (c) => c.toUpperCase());
}

function buildTooltip(s: ProposalSession): string {
  const stepCount = s.stepGroups.reduce((count, group) => count + group.steps.length, 0);
  const lines: string[] = [
    `Session: ${s.sessionId}`,
    `Title: ${s.title || 'n/a'}`,
    `Author: ${friendlyName(s.submittedBy)}`,
    `Initiating Human: ${friendlyName(s.initiatingHumanDisplay || s.initiatingHumanEmail || s.submittedBy) || (s.initiatingHumanSubject || 'n/a')}`,
    `Submitted: ${relativeTime(s.submittedAt)}`,
    `Status: ${s.status ?? 'open'}`,
    `Steps: ${stepCount}`,
  ];
  if (s.dependsUponSession) {
    lines.push(`Depends on: ${s.dependsUponSession}`);
  }
  if (s.errorStage) {
    lines.push(`Error stage: ${s.errorStage}`);
  }
  if (s.erroredAt) {
    lines.push(`Errored: ${relativeTime(s.erroredAt)}`);
  }
  if (s.errorDetail) {
    lines.push(`Error detail: ${s.errorDetail}`);
  }
  return lines.join('\n');
}

function mapTreeNode(tree: SessionTreeModel | null): SessionTreeNode {
  return {
    session: tree?.session ?? null,
    children: (tree?.branches ?? []).map(mapTreeNode),
  };
}

// ---------------------------------------------------------------------------
// Tree node renderer
// ---------------------------------------------------------------------------

const TreeNodeView: React.FC<{
  node: SessionTreeNode;
  depth: number;
  activeSessionId?: string | null;
  onResume?: (sessionId: string) => void;
  onCancel?: (sessionId: string) => void;
  onModify?: (parentSessionId: string) => void;
  onApprove?: (sessionId: string) => void;
  onReject?: (sessionId: string) => void;
  onSubmitSession?: () => void;
  onCancelActiveSession?: () => void;
  approveBlockedSessionId?: string;
  rejectModeSessionId?: string;
  busy?: boolean;
  expandedSummarySessionIds?: Record<string, boolean>;
  expandedStepsSessionIds?: Record<string, boolean>;
  sessionSummaries?: Record<string, string>;
  sessionStepContent?: Record<string, React.ReactNode>;
  onToggleSummary?: (sessionId: string) => void;
  onToggleSteps?: (sessionId: string) => void;
  onShowSourceText?: (sessionId: string) => void;
  onShowTrace?: (sessionId: string) => void;
}> = ({ node, depth, activeSessionId, onResume, onCancel, onModify, onApprove, onReject, onSubmitSession, onCancelActiveSession, approveBlockedSessionId, rejectModeSessionId, busy, expandedSummarySessionIds, expandedStepsSessionIds, sessionSummaries, sessionStepContent, onToggleSummary, onToggleSteps, onShowSourceText, onShowTrace }) => {
  const isRoot = node.session === null || node.session?.sessionId === 'live';
  const sid = isRoot ? null : node.session!.sessionId;
  const status = node.session?.status ?? 'open';
  const isOpen = status === 'open';
  const isSubmitted = status === 'submitted';
  const isError = status === 'error';
  const dependsOnLive = node.session?.dependsUponSession === 'live';
  const isImmutable = !isOpen;
  const isActive = !isRoot && sid === activeSessionId;
  const rejectActionLabel = rejectModeSessionId === sid ? 'Reject' : 'Reset';
  // Always show action buttons for root, submitted, active, and immutable (approved/rejected) session rows.
  const alwaysShowActions = isRoot || isSubmitted || isActive || isImmutable;
  const showSummaryLink = !isRoot && Boolean(onToggleSummary);
  const showStepsLink = !isRoot && Boolean(onToggleSteps);
  const showSourceTextLink = !isRoot && Boolean(onShowSourceText);
  const showTraceLink = !isRoot && Boolean(onShowTrace);
  const showSummary = !isRoot && !!sid && Boolean(expandedSummarySessionIds?.[sid]);
  const showSteps = !isRoot && !!sid && Boolean(expandedStepsSessionIds?.[sid]);
  const summaryMarkdown = !isRoot && sid ? sessionSummaries?.[sid] : undefined;
  const stepsContent = !isRoot && sid ? sessionStepContent?.[sid] : undefined;
  const rowTitle = isRoot ? 'Live Graph' : buildTooltip(node.session!);
  const rowClassName = [
    'st-node-row',
    !isRoot ? 'st-node-row--stacked' : '',
    !isRoot ? 'st-node-row--session' : 'st-node-row--root',
    isActive ? 'st-node-row--active' : '',
  ].filter(Boolean).join(' ');
  const toggleLabel = (label: string, expanded: boolean): string => `${label} ${expanded ? '▾' : '▸'}`;

  return (
    <div className="st-node" style={{ paddingLeft: depth * 16 }}>
      <div className={rowClassName}>
        <div
          className={`st-node-btn${!isRoot ? ' st-node-btn--static' : ''}`}
          title={rowTitle}
          role={isRoot ? undefined : 'group'}
        >
          {isRoot ? (
            <span className="st-node-label">Live Graph</span>
          ) : (
            <>
              <span className="st-node-label">{node.session!.title || formatTime(node.session!.submittedAt)}</span>
            </>
          )}
        </div>

        {isRoot ? (
          <span className={`st-node-actions${alwaysShowActions ? ' st-node-actions--visible' : ''}`}>
            {onModify && (
              <button
                className="st-action-btn"
                title="Branch a new edit session"
                disabled={busy}
                onClick={(e) => { e.stopPropagation(); onModify('live'); }}
              >
                <i className="modus-icons" aria-hidden="true">flowchart</i>
              </button>
            )}
          </span>
        ) : (
          <>
            {isError && (
              <div className="st-node-error" role="note" aria-label="Session error details">
                <strong>Failed</strong>
                {node.session!.errorStage ? ` at ${node.session!.errorStage}` : ''}
                {node.session!.erroredAt ? ` · ${relativeTime(node.session!.erroredAt)}` : ''}
                {node.session!.errorDetail ? ` · ${node.session!.errorDetail}` : ''}
              </div>
            )}

            <div className="st-node-subrow">
              <div className="st-node-inline-links">
                {showSummaryLink && (
                  <button
                    className="st-node-inline-link"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (sid) {
                        onToggleSummary?.(sid);
                      }
                    }}
                  >
                    {toggleLabel('Summary', showSummary)}
                  </button>
                )}
                {showSummaryLink && showStepsLink && <span className="st-node-inline-separator" aria-hidden="true">|</span>}
                {showStepsLink && (
                  <button
                    className="st-node-inline-link"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (sid) {
                        onToggleSteps?.(sid);
                      }
                    }}
                  >
                    {toggleLabel('Steps', showSteps)}
                  </button>
                )}
                {(showSummaryLink || showStepsLink) && showSourceTextLink && (
                  <span className="st-node-inline-separator" aria-hidden="true">|</span>
                )}
                {showSourceTextLink && (
                  <button
                    className="st-node-inline-link"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (sid) {
                        onShowSourceText?.(sid);
                      }
                    }}
                  >
                    Source
                  </button>
                )}
                {(showSummaryLink || showStepsLink || showSourceTextLink) && showTraceLink && (
                  <span className="st-node-inline-separator" aria-hidden="true">|</span>
                )}
                {showTraceLink && (
                  <button
                    className="st-node-inline-link"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (sid) {
                        onShowTrace?.(sid);
                      }
                    }}
                  >
                    Trace
                  </button>
                )}
              </div>
              <span className="st-node-actions st-node-actions--visible">
                {isActive && onSubmitSession && (
                  <button
                    className="st-action-btn st-action-btn--approve"
                    title="Submit session"
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); onSubmitSession(); }}
                  >
                    <i className="modus-icons" aria-hidden="true">check</i>
                  </button>
                )}
                {isActive && onCancelActiveSession && (
                  <button
                    className="st-action-btn st-action-btn--danger"
                    title="Cancel session"
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); onCancelActiveSession(); }}
                  >
                    <i className="modus-icons" aria-hidden="true">cancel_circle</i>
                  </button>
                )}
                {isOpen && !isActive && onResume && (
                  <button
                    className="st-action-btn"
                    title="Resume"
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); onResume(node.session!.sessionId); }}
                  >
                    <i className="modus-icons" aria-hidden="true">pencil</i>
                  </button>
                )}
                {(isOpen || isError) && !isActive && onCancel && (
                  <button
                    className="st-action-btn st-action-btn--danger"
                    title="Cancel"
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); onCancel(node.session!.sessionId); }}
                  >
                    <i className="modus-icons" aria-hidden="true">cancel_circle</i>
                  </button>
                )}
                {isSubmitted && dependsOnLive && onApprove && (
                  <button
                    className="st-action-btn st-action-btn--approve"
                    title={approveBlockedSessionId === sid ? 'Cannot approve — contains rejected steps' : 'Approve'}
                    disabled={busy || approveBlockedSessionId === sid}
                    onClick={(e) => { e.stopPropagation(); onApprove(node.session!.sessionId); }}
                  >
                    <i className="modus-icons" aria-hidden="true">check</i>
                  </button>
                )}
                {isSubmitted && onReject && (
                  <button
                    className="st-action-btn st-action-btn--danger"
                    title={rejectActionLabel}
                    aria-label={rejectActionLabel}
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); onReject(node.session!.sessionId); }}
                  >
                    <i className="modus-icons" aria-hidden="true">cancel_circle</i>
                  </button>
                )}
                {isImmutable && onModify && status !== 'rejected' && (
                  <button
                    className="st-action-btn"
                    title="Branch a new edit session"
                    disabled={busy}
                    onClick={(e) => { e.stopPropagation(); onModify(node.session!.sessionId); }}
                  >
                    <i className="modus-icons" aria-hidden="true">flowchart</i>
                  </button>
                )}
              </span>
            </div>

            {showSummary && summaryMarkdown && (
              <div className="st-node-panel st-node-panel--summary">
                <div className="st-node-panel-body">
                  <Markdown remarkPlugins={[remarkGfm]}>{summaryMarkdown}</Markdown>
                </div>
              </div>
            )}

            {showSteps && stepsContent && (
              <div className="st-node-panel st-node-panel--steps">
                {stepsContent}
              </div>
            )}
          </>
        )}
      </div>

      {node.children.map((child) => (
        <TreeNodeView
          key={child.session!.sessionId}
          node={child}
          depth={depth + 1}
          activeSessionId={activeSessionId}
          onResume={onResume}
          onCancel={onCancel}
          onModify={onModify}
          onApprove={onApprove}
          onReject={onReject}
          onSubmitSession={onSubmitSession}
          onCancelActiveSession={onCancelActiveSession}
          approveBlockedSessionId={approveBlockedSessionId}
          rejectModeSessionId={rejectModeSessionId}
          busy={busy}
          expandedSummarySessionIds={expandedSummarySessionIds}
          expandedStepsSessionIds={expandedStepsSessionIds}
          sessionSummaries={sessionSummaries}
          sessionStepContent={sessionStepContent}
          onToggleSummary={onToggleSummary}
          onToggleSteps={onToggleSteps}
          onShowSourceText={onShowSourceText}
          onShowTrace={onShowTrace}
        />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const SessionTree: React.FC<SessionTreeProps> = ({
  tree, activeSessionId,
  expandedSummarySessionIds, expandedStepsSessionIds, sessionSummaries, sessionStepContent, onToggleSummary, onToggleSteps, onShowSourceText, onShowTrace,
  onResume, onCancel, onModify, onApprove, onReject,
  onSubmitSession, onCancelActiveSession, approveBlockedSessionId, rejectModeSessionId, busy,
}) => {
  const root = mapTreeNode(tree);

  return (
    <div className="session-tree">
      <TreeNodeView
        node={root}
        depth={0}
        activeSessionId={activeSessionId}
        onResume={onResume}
        onCancel={onCancel}
        onModify={onModify}
        onApprove={onApprove}
        onReject={onReject}
        onSubmitSession={onSubmitSession}
        onCancelActiveSession={onCancelActiveSession}
        approveBlockedSessionId={approveBlockedSessionId}
        rejectModeSessionId={rejectModeSessionId}
        busy={busy}
        expandedSummarySessionIds={expandedSummarySessionIds}
        expandedStepsSessionIds={expandedStepsSessionIds}
        sessionSummaries={sessionSummaries}
        sessionStepContent={sessionStepContent}
        onToggleSummary={onToggleSummary}
        onToggleSteps={onToggleSteps}
        onShowSourceText={onShowSourceText}
        onShowTrace={onShowTrace}
      />
    </div>
  );
};

export default SessionTree;
